<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useCrmReportsStore } from 'dashboard/stores/crmReports';
import CrmReportsAPI from 'dashboard/api/crmReports';
import CrmDealsAPI from 'dashboard/api/crmDeals';
import { dealToReportRow } from './dealToReportRow';
import CashDealsModal from './CashDealsModal.vue';

export default {
  name: 'CrmReportsView',
  components: { CashDealsModal },
  setup() {
    const crmReportsStore = useCrmReportsStore();
    return { crmReportsStore };
  },
  data() {
    return {
      loading: true,
      // Linhas do modo conversational. Ficam aqui, e não na lista de conversas
      // do Vuex, que é a da caixa de entrada.
      conversationRows: [],
      // Recebido e previsto do período vêm do servidor: dependem das parcelas,
      // que não viajam na lista de negócios, e a regra (ganho sem parcela conta
      // como à vista) mora em Crm::Reports::CashSql, a mesma dos relatórios
      // personalizados.
      cash: null,
      // 'received' ou 'forecast' enquanto a lista de quem pagou está aberta.
      cashList: null,
    };
  },
  watch: {
    cashParams: {
      handler() {
        this.fetchCash();
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters({
      currentAccountId: 'getCurrentAccountId',
    }),
    agents() {
      return this.$store.getters['agents/getAgents'] || [];
    },
    periodFilter() {
      return this.crmReportsStore.filters.period;
    },
    // 'created' recorta por quando o lead entrou; 'closed' por quando fechou.
    // Por fechamento, lead aberto não tem data e fica de fora, então "ativos"
    // é zero e a conversão vira ganhos sobre ganhos+perdidos — a mesma regra
    // do servidor (Crm::Reports::DealScope#apply_period).
    byClosedAt() {
      return this.crmReportsStore.filters.dateField === 'closed';
    },
    customDateFrom() {
      return this.crmReportsStore.filters.dateFrom || '';
    },
    customDateTo() {
      return this.crmReportsStore.filters.dateTo || '';
    },
    agentFilter() {
      return this.crmReportsStore.filters.agentId;
    },
    inboxFilter() {
      return this.crmReportsStore.filters.inboxId;
    },
    // Campos da conversa escolhidos no cabeçalho (ex.: { marca: 'Fiat' }).
    convAttrFilter() {
      return Object.entries(
        this.crmReportsStore.filters.convAttrs || {}
      ).filter(([, value]) => value);
    },
    // Etiquetas das colunas do funil escolhido no cabeçalho. As abas Por
    // Origem/Plataforma/Anúncio só contam conversa com uma delas
    // (OriginAggregatorService#apply_funnel_filter); sem isso a Geral somava
    // conversas de outros funis e de etiquetas soltas. Lista vazia = sem filtro.
    funnelLabelTitles() {
      const account = this.$store.getters['accounts/getAccount'](
        this.currentAccountId
      );
      const funnels = account?.custom_attributes?.crm_funnels || [];
      const funnelId = this.crmReportsStore.filters.funnelId;
      if (!funnelId || !funnels.length) return null;
      const funnel =
        funnels.find(f => String(f.id) === String(funnelId)) || funnels[0];
      const labels = this.$store.getters['labels/getLabels'] || [];
      const titles = (funnel.columns || [])
        .map(ref => {
          const byId = labels.find(l => String(l.id) === String(ref));
          return byId ? byId.title : labels.find(l => l.title === ref)?.title;
        })
        .filter(Boolean);
      return titles.length ? new Set(titles) : null;
    },
    // Period is filtered by created_at (when the lead entered) to keep
    // consistency with the Origem/Plataforma/Anúncio tabs and with the
    // new conversion-rate definition (won / total_in_period). Closure
    // date used to be the filter here, which caused leads counted as
    // "won" in this period when they entered in a prior period.
    isDealsMode() {
      const account = this.$store.getters['accounts/getAccount'](
        this.currentAccountId
      );
      return account?.crm_mode === 'deals';
    },
    // Fonte do relatório. Em modo deals são os negócios, adaptados para o
    // formato de conversa que o resto desta tela já lê; fora dele, as conversas
    // como sempre foi.
    sourceRows() {
      if (!this.isDealsMode) return this.conversationRows;
      return (this.$store.getters['crmDeals/getDeals'] || []).map(
        dealToReportRow
      );
    },
    filteredConversations() {
      return this.sourceRows.filter(c => {
        const status = c.custom_attributes?.deal_status;
        if (!status) return false;
        if (!this.matchesInboxAndFunnel(c)) return false;
        if (!this.inPeriod(c)) return false;

        if (this.agentFilter !== null) {
          const assigneeId = c.meta?.assignee?.id;
          if (assigneeId !== this.agentFilter) return false;
        }

        return true;
      });
    },
    // Won deals CLOSED inside the selected period (regardless of when the
    // lead entered). Powers the "Fechamentos no Período" section so the
    // admin can also see month-end revenue numbers alongside the
    // entry-based metrics above.
    closedInPeriod() {
      return this.sourceRows
        .filter(c => {
          if (c.custom_attributes?.deal_status !== 'won') return false;

          const closedIso = c.custom_attributes?.deal_closed_at;
          if (!closedIso) return false;
          if (!this.matchesInboxAndFunnel(c)) return false;

          if (this.dateRange) {
            const closed = new Date(closedIso);
            if (closed < this.dateRange.from || closed > this.dateRange.to) {
              return false;
            }
          }

          if (this.agentFilter !== null) {
            const assigneeId = c.meta?.assignee?.id;
            if (assigneeId !== this.agentFilter) return false;
          }
          return true;
        })
        .map(c => this.mapDeal(c));
    },
    closedInPeriodTotal() {
      return this.closedInPeriod.reduce((acc, d) => acc + d.rawValue, 0);
    },
    formattedClosedInPeriodTotal() {
      return this.formatCurrency(this.closedInPeriodTotal);
    },
    cashParams() {
      const params = { period: this.periodFilter };
      if (this.periodFilter === 'custom') {
        params.date_from = this.customDateFrom;
        params.date_to = this.customDateTo;
      }
      if (this.agentFilter) params.agent_id = this.agentFilter;
      if (this.inboxFilter) params.inbox_id = this.inboxFilter;
      if (this.convAttrFilter.length) {
        params.conv_attrs = Object.fromEntries(this.convAttrFilter);
      }
      return params;
    },
    formattedCashReceived() {
      return this.formatCurrency((this.cash?.received_cents || 0) / 100);
    },
    formattedCashForecast() {
      return this.formatCurrency((this.cash?.forecast_cents || 0) / 100);
    },
    dateRange() {
      const now = new Date();
      const to = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        59,
        59
      );
      let from = null;

      switch (this.periodFilter) {
        case 'today':
          from = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            0,
            0,
            0
          );
          break;
        case 'current_month':
          from = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
          break;
        // Janela móvel de N x 24h até agora, como o servidor
        // (OriginAggregatorService#period_range: 7.days.ago..now).
        case '7d':
          from = new Date(now.getTime() - 7 * 86400000);
          break;
        case '30d':
          from = new Date(now.getTime() - 30 * 86400000);
          break;
        case '90d':
          from = new Date(now.getTime() - 90 * 86400000);
          break;
        case 'custom':
          if (this.customDateFrom && this.customDateTo) {
            from = new Date(this.customDateFrom + 'T00:00:00');
            return { from, to: new Date(this.customDateTo + 'T23:59:59') };
          }
          return null;
        case 'all':
        default:
          return null;
      }

      return from ? { from, to } : null;
    },
    wonDeals() {
      return this.filteredConversations
        .filter(c => c.custom_attributes?.deal_status === 'won')
        .map(c => this.mapDeal(c))
        .sort((a, b) => new Date(b.rawClosedAt) - new Date(a.rawClosedAt));
    },
    lostDeals() {
      return this.filteredConversations
        .filter(c => c.custom_attributes?.deal_status === 'lost')
        .map(c => this.mapDeal(c))
        .sort((a, b) => new Date(b.rawClosedAt) - new Date(a.rawClosedAt));
    },
    // Active leads = conversations in some kanban column (has at least one
    // label) that aren't won/lost yet. Filtered by created_at against the
    // period so the KPI matches what the user sees on screen.
    activeLeads() {
      // Por fechamento não existe lead ativo no recorte: só quem tem data de
      // fechamento entra no período.
      if (this.byClosedAt) return [];
      return this.sourceRows.filter(c => {
        const status = c.custom_attributes?.deal_status;
        if (status === 'won' || status === 'lost') return false;
        // A etiqueta é o que põe a conversa numa coluna do quadro. O negócio já
        // nasce numa etapa, então exigir etiqueta dele zeraria os leads ativos —
        // e a taxa de conversão, que os tem no denominador, sairia inflada.
        if (!c.is_deal && !(c.labels || []).length) return false;
        if (!this.matchesInboxAndFunnel(c)) return false;

        if (this.dateRange && c.created_at) {
          const created = new Date(c.created_at * 1000);
          if (created < this.dateRange.from || created > this.dateRange.to) {
            return false;
          }
        }

        if (this.agentFilter !== null) {
          const assigneeId = c.meta?.assignee?.id;
          if (assigneeId !== this.agentFilter) return false;
        }

        return true;
      });
    },
    totalDeals() {
      return this.wonDeals.length + this.lostDeals.length;
    },
    // Total leads in period = won + lost + still active.
    // Used as the denominator for the new conversion rate formula.
    totalLeadsInPeriod() {
      return (
        this.wonDeals.length + this.lostDeals.length + this.activeLeads.length
      );
    },
    wonTotal() {
      return this.wonDeals.reduce((acc, d) => acc + d.rawValue, 0);
    },
    lostTotal() {
      return this.lostDeals.reduce((acc, d) => acc + d.rawValue, 0);
    },
    formattedWonTotal() {
      return this.formatCurrency(this.wonTotal);
    },
    formattedLostTotal() {
      return this.formatCurrency(this.lostTotal);
    },
    // won / total_leads_in_period (includes still-active in denominator).
    // See OriginAggregatorService#aggregate_kpis for the rationale.
    conversionRate() {
      if (this.totalLeadsInPeriod === 0) return 0;
      return ((this.wonDeals.length / this.totalLeadsInPeriod) * 100).toFixed(
        1
      );
    },
    totalPaid() {
      return this.wonDeals.reduce((acc, d) => acc + d.paidValue, 0);
    },
    totalReceivable() {
      return this.wonDeals.reduce((acc, d) => acc + d.receivableValue, 0);
    },
    formattedTotalPaid() {
      return this.formatCurrency(this.totalPaid);
    },
    formattedTotalReceivable() {
      return this.formatCurrency(this.totalReceivable);
    },
    pendingReceivableCount() {
      return this.wonDeals.filter(d => d.receivableValue > 0).length;
    },
    dealsByAgent() {
      const map = {};
      this.wonDeals.forEach(deal => {
        const key = deal.agentId || 'unassigned';
        if (!map[key]) {
          map[key] = {
            agentId: key,
            name: deal.agentName || 'Não atribuído',
            initials: this.getInitials(deal.agentName || 'NA'),
            wonCount: 0,
            totalValue: 0,
          };
        }
        map[key].wonCount += 1;
        map[key].totalValue += deal.rawValue;
      });

      const agents = Object.values(map).sort(
        (a, b) => b.totalValue - a.totalValue
      );
      const maxVal = agents.length > 0 ? agents[0].totalValue : 1;

      return agents.map(a => ({
        ...a,
        formattedTotal: this.formatCurrency(a.totalValue),
        barWidth: Math.max(20, Math.round((a.totalValue / maxVal) * 100)) + '%',
      }));
    },
    wonDealsByPaymentMethod() {
      const map = {};
      this.wonDeals.forEach(deal => {
        const key = deal.paymentMethod || 'Não informado';
        if (!map[key]) {
          map[key] = { label: key, count: 0, totalValue: 0 };
        }
        map[key].count += 1;
        map[key].totalValue += deal.rawValue;
      });
      const methods = Object.values(map).sort(
        (a, b) => b.totalValue - a.totalValue
      );
      const maxVal = methods.length > 0 ? methods[0].totalValue : 1;
      return methods.map(m => ({
        ...m,
        formattedTotal: this.formatCurrency(m.totalValue),
        barWidth: Math.max(20, Math.round((m.totalValue / maxVal) * 100)) + '%',
      }));
    },
    lostDealsByReason() {
      const map = {};
      this.lostDeals.forEach(deal => {
        const key = deal.lostReason || 'Não informado';
        if (!map[key]) {
          map[key] = { label: key, count: 0, totalValue: 0 };
        }
        map[key].count += 1;
        map[key].totalValue += deal.rawValue;
      });
      const reasons = Object.values(map).sort((a, b) => b.count - a.count);
      const maxCount = reasons.length > 0 ? reasons[0].count : 1;
      return reasons.map(r => ({
        ...r,
        formattedTotal: this.formatCurrency(r.totalValue),
        barWidth: Math.max(10, Math.round((r.count / maxCount) * 100)) + '%',
      }));
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('labels/get');
    this.fetchAllConversations();
    this.fetchCash();
  },
  methods: {
    // A linha cai dentro do período pela data escolhida no cabeçalho. Sem
    // recorte ("todo o período") tudo entra, menos, por fechamento, quem ainda
    // não fechou.
    inPeriod(row) {
      if (this.byClosedAt) {
        const closedIso = row.custom_attributes?.deal_closed_at;
        if (!closedIso) return false;
        if (!this.dateRange) return true;
        const closed = new Date(closedIso);
        return closed >= this.dateRange.from && closed <= this.dateRange.to;
      }
      if (!this.dateRange || !row.created_at) return true;
      const created = new Date(row.created_at * 1000);
      return created >= this.dateRange.from && created <= this.dateRange.to;
    },
    async fetchCash() {
      if (!this.isDealsMode) return;
      try {
        const { data } = await CrmReportsAPI.getCash(this.cashParams);
        this.cash = data;
      } catch {
        this.cash = null;
      }
    },
    fetchCashDeals() {
      return CrmReportsAPI.getCashDeals({
        ...this.cashParams,
        kind: this.cashList,
      });
    },
    async fetchAllConversations() {
      // Em modo deals a fonte é a API do CRM: nada de varrer conversas nem de
      // mexer na lista compartilhada do Vuex, que é a da caixa de entrada.
      if (this.isDealsMode) {
        this.loading = true;
        try {
          await Promise.all([
            this.$store.dispatch('crmDeals/fetchDeals', { per_page: 500 }),
            this.$store.dispatch('crmDeals/fetchOrigins'),
            this.$store.dispatch('crmDeals/fetchPaymentMethods'),
            this.$store.dispatch('crmDeals/fetchLostReasons'),
          ]);
        } finally {
          this.loading = false;
        }
        return;
      }

      // Só as conversas que o relatório conta (com desfecho ou em alguma
      // etapa), no formato enxuto do quadro, 500 por pedido e todos os status
      // de uma vez. Antes a tela varria a conta inteira pela lista da caixa de
      // entrada, 50 por pedido e um status por vez.
      // Adiadas entram: na Marajó, por exemplo, lead perdido vai para adiada, e
      // deixá-las de fora sumia com os perdidos. As outras abas contam todos
      // os status.
      const PAGE_SIZE = 500;
      this.loading = true;
      try {
        const rows = [];
        for (let page = 1; page <= 40; page += 1) {
          // eslint-disable-next-line no-await-in-loop
          const { data } = await CrmDealsAPI.boardCards({
            for_report: true,
            status: 'all',
            assignee_type: 'all',
            page,
            per_page: PAGE_SIZE,
          });
          rows.push(...(data.payload || []));
          if (!data.meta?.has_more) break;
        }
        this.conversationRows = rows;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Relatório Geral] Falha ao carregar os leads:', error);
      } finally {
        this.loading = false;
      }
    },
    mapDeal(conversation) {
      const attrs = conversation.custom_attributes || {};
      const val = parseFloat(attrs.deal_value) || 0;
      // Ganho sem parcelas é à vista (mesma regra do servidor em
      // Crm::Reports::CashSql): o valor cheio conta como recebido. Só vale no
      // modo deals; no conversational o contador nem existe.
      const aVista =
        attrs.deal_status === 'won' && attrs.deal_installments_count === 0;
      const paidVal = aVista ? val : parseFloat(attrs.deal_paid_value) || 0;
      const receivableVal = Math.max(0, val - paidVal);
      const closedAt = attrs.deal_closed_at;
      const contactName = conversation.meta?.sender?.name || 'Desconhecido';
      return {
        id: conversation.id,
        contactName,
        // A coluna passa a mostrar o NEGÓCIO; o contato vira a linha de apoio.
        // Negócio sem título cai no nome do contato, que é o que existe.
        dealName: attrs.deal_title || contactName,
        // O adaptador do negócio carrega só o id do responsável; o nome vem da
        // lista de agentes. No modo conversational o payload já traz os dois.
        agentName:
          conversation.meta?.assignee?.name ||
          this.agentNameById(conversation.meta?.assignee?.id) ||
          'Não atribuído',
        agentId: conversation.meta?.assignee?.id || null,
        rawValue: val,
        formattedValue: this.formatCurrency(val),
        paidValue: paidVal,
        formattedPaidValue: this.formatCurrency(paidVal),
        receivableValue: receivableVal,
        formattedReceivableValue: this.formatCurrency(receivableVal),
        rawClosedAt: closedAt || '',
        closedDate: closedAt
          ? new Date(closedAt).toLocaleDateString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })
          : '-',
        paymentMethod:
          attrs.deal_payment_method ||
          this.nameFromList(
            'crmDeals/getPaymentMethods',
            attrs.deal_payment_method_id
          ),
        lostReason:
          attrs.deal_lost_reason ||
          this.nameFromList(
            'crmDeals/getLostReasons',
            attrs.deal_lost_reason_id
          ),
        origin:
          attrs.deal_origin ||
          this.nameFromList('crmDeals/getOrigins', attrs.deal_origin_id),
      };
    },
    // Mesmo recorte das outras abas: caixa escolhida e etiqueta do funil. A
    // caixa vale nos dois modos; no deals é a da conversa de origem, e negócio
    // sem conversa sai do recorte, como no DealsAggregatorService. O funil por
    // etiqueta é só do modo conversational: o negócio não tem etiqueta.
    matchesInboxAndFunnel(c) {
      if (this.inboxFilter && c.inbox_id !== this.inboxFilter) return false;
      // No deals os campos da conversa vêm em conversation_attributes (os
      // custom_attributes da linha são do negócio); no conversational a linha
      // já é a conversa.
      const convAttrs = c.is_deal
        ? c.conversation_attributes
        : c.custom_attributes;
      if (
        this.convAttrFilter.some(([key, value]) => convAttrs?.[key] !== value)
      ) {
        return false;
      }
      if (this.isDealsMode || !this.funnelLabelTitles) return true;
      return (c.labels || []).some(l => this.funnelLabelTitles.has(l));
    },
    // No modo deals origem, forma de pagamento e motivo da perda são id; o nome
    // vive nas listas de configuração do CRM.
    agentNameById(id) {
      if (!id) return '';
      return this.agents.find(a => a.id === id)?.name || '';
    },
    // Quem administra o CRM marca a venda, mas o crédito é do vendedor: dá pra
    // corrigir o responsável direto na linha, sem abrir o negócio.
    async updateDealAssignee(deal, event) {
      const value = event.target.value;
      try {
        await this.$store.dispatch('crmDeals/updateDeal', {
          id: deal.id,
          deal: { assignee_id: value || null },
        });
        await this.$store.dispatch('crmDeals/fetchDeals', { per_page: 500 });
      } catch {
        useAlert('Não foi possível alterar o responsável.');
      }
    },
    nameFromList(getter, id) {
      if (!id) return '';
      const item = (this.$store.getters[getter] || []).find(i => i.id === id);
      return item?.name || '';
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(value);
    },
    getInitials(name) {
      if (!name) return '?';
      const parts = name.trim().split(' ');
      return parts.length > 1
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : name.substring(0, 2).toUpperCase();
    },
    navigateToConversation(id) {
      this.$router.push({
        name: 'inbox_conversation',
        params: {
          accountId: this.$route.params.accountId,
          conversation_id: id,
        },
      });
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-full bg-n-background">
    <!-- Content -->
    <div class="flex-1 overflow-y-auto p-6">
      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center py-20">
        <span class="text-sm text-n-slate-11">Carregando dados...</span>
      </div>

      <template v-else>
        <!-- Metric Cards -->
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
          <div
            class="rounded-xl border border-n-weak bg-n-surface-1 p-5 min-w-0"
          >
            <p
              class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide m-0 mb-2"
            >
              🔥 Leads Ativos
            </p>
            <p class="text-2xl font-bold text-n-slate-12 m-0">
              {{ activeLeads.length }}
            </p>
            <p class="text-xs text-n-slate-11 m-0 mt-1">
              em andamento no funil
            </p>
          </div>
          <div
            class="rounded-xl border border-n-blue-6 bg-n-blue-2 p-5 min-w-0"
          >
            <p
              class="text-xs font-semibold text-n-blue-11 uppercase tracking-wide m-0 mb-2"
            >
              🏆 Ganhos
            </p>
            <p class="text-2xl font-bold text-n-blue-11 m-0">
              {{ wonDeals.length }}
            </p>
            <p class="text-sm font-semibold text-n-blue-11 m-0 mt-1 truncate">
              {{ formattedWonTotal }}
            </p>
          </div>
          <div
            class="rounded-xl border border-n-ruby-6 bg-n-ruby-2 p-5 min-w-0"
          >
            <p
              class="text-xs font-semibold text-n-ruby-11 uppercase tracking-wide m-0 mb-2"
            >
              ❌ Perdidos
            </p>
            <p class="text-2xl font-bold text-n-ruby-11 m-0">
              {{ lostDeals.length }}
            </p>
            <p class="text-sm font-semibold text-n-ruby-11 m-0 mt-1 truncate">
              {{ formattedLostTotal }}
            </p>
          </div>
          <div
            class="rounded-xl border border-n-blue-6 bg-n-blue-2 p-5 min-w-0"
          >
            <p
              class="text-xs font-semibold text-n-blue-11 uppercase tracking-wide m-0 mb-2"
            >
              📈 Taxa de Conversão
            </p>
            <p class="text-2xl font-bold text-n-blue-11 m-0">
              {{ conversionRate }}%
            </p>
            <p class="text-xs text-n-slate-11 m-0 mt-1">
              {{ wonDeals.length }} ganho(s) de {{ totalLeadsInPeriod }} lead(s)
              no período
            </p>
          </div>
          <div
            class="rounded-xl border border-emerald-600/30 bg-emerald-500/10 p-5 min-w-0"
          >
            <p
              class="text-xs font-semibold text-emerald-500 uppercase tracking-wide m-0 mb-2"
            >
              💰 Total Recebido
            </p>
            <p class="text-xl font-bold text-emerald-500 m-0 truncate">
              {{ formattedTotalPaid }}
            </p>
            <p class="text-xs text-n-slate-11 m-0 mt-1">
              de {{ wonDeals.length }} negócio(s) ganho(s)
            </p>
          </div>
          <div
            class="rounded-xl border border-amber-500/30 bg-amber-500/10 p-5 min-w-0"
          >
            <p
              class="text-xs font-semibold text-amber-500 uppercase tracking-wide m-0 mb-2"
            >
              📋 A Receber
            </p>
            <p class="text-xl font-bold text-amber-500 m-0 truncate">
              {{ formattedTotalReceivable }}
            </p>
            <p class="text-xs text-n-slate-11 m-0 mt-1">
              {{ pendingReceivableCount }} pendente(s)
            </p>
          </div>
        </div>

        <!-- Fechamentos no Período (closed_at-based view) -->
        <div class="mb-8">
          <h2 class="text-base font-bold text-n-slate-12 m-0 mb-2">
            💰 Fechamentos no Período
          </h2>
          <p class="text-xs text-n-slate-11 m-0 mb-4">
            Negócios fechados (ganhos) com data de fechamento dentro do período,
            independente de quando o lead entrou.
            <template v-if="isDealsMode">
              Recebido e previsto seguem as parcelas: recebido é o que foi pago
              no período, previsto é o que vence nele. Ganho sem parcelas conta
              como à vista na data do ganho.
            </template>
          </p>
          <div
            class="grid grid-cols-1 md:grid-cols-2 gap-4"
            :class="{ 'lg:grid-cols-4': isDealsMode }"
          >
            <div
              class="rounded-xl border border-emerald-600/30 bg-emerald-500/10 p-5 min-w-0"
            >
              <p
                class="text-xs font-semibold text-emerald-500 uppercase tracking-wide m-0 mb-2"
              >
                🏆 Negócios Fechados
              </p>
              <p class="text-2xl font-bold text-emerald-500 m-0">
                {{ closedInPeriod.length }}
              </p>
            </div>
            <div
              class="rounded-xl border border-emerald-600/30 bg-emerald-500/10 p-5 min-w-0"
            >
              <p
                class="text-xs font-semibold text-emerald-500 uppercase tracking-wide m-0 mb-2"
              >
                💵 Vendido no Período
              </p>
              <p class="text-2xl font-bold text-emerald-500 m-0 truncate">
                {{ formattedClosedInPeriodTotal }}
              </p>
              <p class="text-xs text-n-slate-11 m-0 mt-1">valor cheio</p>
            </div>
            <template v-if="isDealsMode">
              <div
                class="rounded-xl border border-emerald-600/30 bg-emerald-500/10 p-5 min-w-0 cursor-pointer hover:border-emerald-500"
                @click="cashList = 'received'"
              >
                <p
                  class="text-xs font-semibold text-emerald-500 uppercase tracking-wide m-0 mb-2"
                >
                  💰 Recebido no Período
                </p>
                <p class="text-2xl font-bold text-emerald-500 m-0 truncate">
                  {{ formattedCashReceived }}
                </p>
                <p class="text-xs text-n-slate-11 m-0 mt-1">
                  parcelas pagas · ver lista
                </p>
              </div>
              <div
                class="rounded-xl border border-amber-500/30 bg-amber-500/10 p-5 min-w-0 cursor-pointer hover:border-amber-500"
                @click="cashList = 'forecast'"
              >
                <p
                  class="text-xs font-semibold text-amber-500 uppercase tracking-wide m-0 mb-2"
                >
                  📅 Previsto no Período
                </p>
                <p class="text-2xl font-bold text-amber-500 m-0 truncate">
                  {{ formattedCashForecast }}
                </p>
                <p class="text-xs text-n-slate-11 m-0 mt-1">
                  parcelas com vencimento · ver lista
                </p>
              </div>
            </template>
          </div>
        </div>

        <!-- Won Deals by Agent -->
        <div class="mb-8">
          <h2 class="text-base font-bold text-n-slate-12 m-0 mb-4">
            Vendas por Agente
          </h2>
          <div v-if="dealsByAgent.length === 0" class="text-sm text-n-slate-11">
            Nenhum negócio ganho no período selecionado.
          </div>
          <div
            v-else
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            <div
              v-for="agentData in dealsByAgent"
              :key="agentData.agentId"
              class="rounded-xl border border-n-weak bg-n-surface-1 p-4"
            >
              <div class="flex items-center gap-3 mb-3">
                <div
                  class="w-9 h-9 rounded-full bg-n-blue-3 flex items-center justify-center text-sm font-bold text-n-blue-11"
                >
                  {{ agentData.initials }}
                </div>
                <div>
                  <p class="text-sm font-semibold text-n-slate-12 m-0">
                    {{ agentData.name }}
                  </p>
                  <p class="text-xs text-n-slate-11 m-0">
                    {{ agentData.wonCount }} venda(s)
                  </p>
                </div>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-lg font-bold text-n-blue-11">{{
                  agentData.formattedTotal
                }}</span>
                <div class="flex items-center gap-1">
                  <div
                    class="h-2 rounded-full bg-n-blue-9"
                    :style="{ width: `${agentData.barWidth}px` }"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Won Deals by Payment Method & Lost Deals by Reason -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <!-- Payment Methods -->
          <div>
            <h2 class="text-base font-bold text-n-slate-12 m-0 mb-4">
              Receita por Forma de Pagamento
            </h2>
            <div
              v-if="wonDealsByPaymentMethod.length === 0"
              class="text-sm text-n-slate-11"
            >
              Sem dados suficientes.
            </div>
            <div v-else class="space-y-3">
              <div
                v-for="(method, idx) in wonDealsByPaymentMethod"
                :key="idx"
                class="flex items-center justify-between p-3 rounded-lg border border-n-weak bg-n-surface-1"
              >
                <div class="flex-1 min-w-0">
                  <div class="flex justify-between items-center mb-1">
                    <span
                      class="text-sm font-semibold text-n-slate-12 truncate pr-2"
                      >{{ method.label }}</span
                    >
                    <span
                      class="text-sm font-bold text-n-blue-11 whitespace-nowrap"
                      >{{ method.formattedTotal }}</span
                    >
                  </div>
                  <div class="flex items-center gap-2">
                    <div
                      class="flex-1 h-1.5 bg-n-alpha-1 rounded-full overflow-hidden"
                    >
                      <div
                        class="h-full bg-n-blue-9 rounded-full"
                        :style="{ width: method.barWidth }"
                      />
                    </div>
                    <span class="text-xs text-n-slate-11 w-16 text-right"
                      >{{ method.count }} venda(s)</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Lost Reasons -->
          <div>
            <h2 class="text-base font-bold text-n-slate-12 m-0 mb-4">
              Motivos de Perda
            </h2>
            <div
              v-if="lostDealsByReason.length === 0"
              class="text-sm text-n-slate-11"
            >
              Sem dados suficientes.
            </div>
            <div v-else class="space-y-3">
              <div
                v-for="(reason, idx) in lostDealsByReason"
                :key="idx"
                class="flex items-center justify-between p-3 rounded-lg border border-n-weak bg-n-surface-1"
              >
                <div class="flex-1 min-w-0">
                  <div class="flex justify-between items-center mb-1">
                    <span
                      class="text-sm font-semibold text-n-slate-12 truncate pr-2"
                      >{{ reason.label }}</span
                    >
                    <span
                      class="text-sm font-bold text-n-ruby-11 whitespace-nowrap"
                      >{{ reason.count }} perda(s)</span
                    >
                  </div>
                  <div class="flex items-center gap-2">
                    <div
                      class="flex-1 h-1.5 bg-n-alpha-1 rounded-full overflow-hidden"
                    >
                      <div
                        class="h-full bg-n-ruby-9 rounded-full"
                        :style="{ width: reason.barWidth }"
                      />
                    </div>
                    <span class="text-xs text-n-slate-11 w-20 text-right"
                      >{{ reason.formattedTotal }} perdidos</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Recent Won Deals List -->
        <div class="mb-8">
          <h2 class="text-base font-bold text-n-slate-12 m-0 mb-4">
            Negócios Recentes (Ganhos)
          </h2>
          <div v-if="wonDeals.length === 0" class="text-sm text-n-slate-11">
            Nenhum negócio ganho no período selecionado.
          </div>
          <div v-else class="rounded-xl border border-n-weak overflow-hidden">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-n-alpha-2">
                  <th class="text-left px-4 py-3 font-semibold text-n-slate-11">
                    Contato
                  </th>
                  <th class="text-left px-4 py-3 font-semibold text-n-slate-11">
                    Agente
                  </th>
                  <th
                    class="text-right px-4 py-3 font-semibold text-n-slate-11"
                  >
                    Valor
                  </th>
                  <th
                    class="text-right px-4 py-3 font-semibold text-n-slate-11"
                  >
                    Pago
                  </th>
                  <th
                    class="text-right px-4 py-3 font-semibold text-n-slate-11"
                  >
                    A Receber
                  </th>
                  <th class="text-left px-4 py-3 font-semibold text-n-slate-11">
                    Pagamento
                  </th>
                  <th class="text-left px-4 py-3 font-semibold text-n-slate-11">
                    Origem
                  </th>
                  <th
                    class="text-right px-4 py-3 font-semibold text-n-slate-11"
                  >
                    Data
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr
                  v-for="deal in wonDeals"
                  :key="deal.id"
                  class="hover:bg-n-alpha-1 cursor-pointer transition-colors"
                  @click="navigateToConversation(deal.id)"
                >
                  <td class="px-4 py-3 text-n-slate-12 font-medium">
                    {{ deal.dealName }}
                    <span
                      v-if="deal.dealName !== deal.contactName"
                      class="block text-xs font-normal text-n-slate-10 truncate"
                    >
                      {{ deal.contactName }}
                    </span>
                  </td>
                  <td class="px-4 py-3 text-n-slate-11" @click.stop>
                    <select
                      v-if="isDealsMode"
                      :value="deal.agentId || ''"
                      class="w-full max-w-[160px] px-2 py-1 rounded-md text-xs bg-n-surface-1 border border-n-weak text-n-slate-12 cursor-pointer"
                      @change="updateDealAssignee(deal, $event)"
                    >
                      <option value="">Não atribuído</option>
                      <option
                        v-for="agent in agents"
                        :key="agent.id"
                        :value="agent.id"
                      >
                        {{ agent.name }}
                      </option>
                    </select>
                    <template v-else>{{ deal.agentName }}</template>
                  </td>
                  <td class="px-4 py-3 text-right font-semibold text-n-blue-11">
                    {{ deal.formattedValue }}
                  </td>
                  <td
                    class="px-4 py-3 text-right font-medium"
                    :class="
                      deal.paidValue > 0 ? 'text-emerald-500' : 'text-n-slate-9'
                    "
                  >
                    {{ deal.paidValue > 0 ? deal.formattedPaidValue : '—' }}
                  </td>
                  <td
                    class="px-4 py-3 text-right font-medium"
                    :class="
                      deal.receivableValue > 0
                        ? 'text-amber-500'
                        : 'text-emerald-500'
                    "
                  >
                    {{ deal.formattedReceivableValue }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-11">
                    <span
                      v-if="deal.paymentMethod"
                      class="inline-flex items-center gap-1 text-xs font-medium bg-n-blue-2 text-n-blue-11 px-2 py-0.5 rounded-full"
                      >{{ deal.paymentMethod }}</span
                    >
                    <span v-else class="text-xs text-n-slate-9">—</span>
                  </td>
                  <td class="px-4 py-3 text-n-slate-11">
                    <span
                      v-if="deal.origin"
                      class="inline-flex items-center gap-1 text-xs font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 px-2 py-0.5 rounded-full"
                      >{{ deal.origin }}</span
                    >
                    <span v-else class="text-xs text-n-slate-9">—</span>
                  </td>
                  <td class="px-4 py-3 text-right text-n-slate-10">
                    {{ deal.closedDate }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Recent Lost Deals List -->
        <div>
          <h2 class="text-base font-bold text-n-slate-12 m-0 mb-4">
            Negócios Recentes (Perdidos)
          </h2>
          <div v-if="lostDeals.length === 0" class="text-sm text-n-slate-11">
            Nenhum negócio perdido no período selecionado.
          </div>
          <div v-else class="rounded-xl border border-n-weak overflow-hidden">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-n-alpha-2">
                  <th class="text-left px-4 py-3 font-semibold text-n-slate-11">
                    Contato
                  </th>
                  <th class="text-left px-4 py-3 font-semibold text-n-slate-11">
                    Agente
                  </th>
                  <th
                    class="text-right px-4 py-3 font-semibold text-n-slate-11"
                  >
                    Valor
                  </th>
                  <th class="text-left px-4 py-3 font-semibold text-n-slate-11">
                    Motivo
                  </th>
                  <th class="text-left px-4 py-3 font-semibold text-n-slate-11">
                    Origem
                  </th>
                  <th
                    class="text-right px-4 py-3 font-semibold text-n-slate-11"
                  >
                    Data
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr
                  v-for="deal in lostDeals"
                  :key="deal.id"
                  class="hover:bg-n-alpha-1 cursor-pointer transition-colors"
                  @click="navigateToConversation(deal.id)"
                >
                  <td class="px-4 py-3 text-n-slate-12 font-medium">
                    {{ deal.dealName }}
                    <span
                      v-if="deal.dealName !== deal.contactName"
                      class="block text-xs font-normal text-n-slate-10 truncate"
                    >
                      {{ deal.contactName }}
                    </span>
                  </td>
                  <td class="px-4 py-3 text-n-slate-11" @click.stop>
                    <select
                      v-if="isDealsMode"
                      :value="deal.agentId || ''"
                      class="w-full max-w-[160px] px-2 py-1 rounded-md text-xs bg-n-surface-1 border border-n-weak text-n-slate-12 cursor-pointer"
                      @change="updateDealAssignee(deal, $event)"
                    >
                      <option value="">Não atribuído</option>
                      <option
                        v-for="agent in agents"
                        :key="agent.id"
                        :value="agent.id"
                      >
                        {{ agent.name }}
                      </option>
                    </select>
                    <template v-else>{{ deal.agentName }}</template>
                  </td>
                  <td class="px-4 py-3 text-right font-semibold text-n-ruby-11">
                    {{ deal.formattedValue }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-11">
                    <span
                      v-if="deal.lostReason"
                      class="inline-flex items-center gap-1 text-xs font-medium bg-n-ruby-2 text-n-ruby-11 px-2 py-0.5 rounded-full"
                      >{{ deal.lostReason }}</span
                    >
                    <span v-else class="text-xs text-n-slate-9">—</span>
                  </td>
                  <td class="px-4 py-3 text-n-slate-11">
                    <span
                      v-if="deal.origin"
                      class="inline-flex items-center gap-1 text-xs font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 px-2 py-0.5 rounded-full"
                      >{{ deal.origin }}</span
                    >
                    <span v-else class="text-xs text-n-slate-9">—</span>
                  </td>
                  <td class="px-4 py-3 text-right text-n-slate-10">
                    {{ deal.closedDate }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </div>

    <CashDealsModal
      v-if="cashList"
      :kind="cashList"
      :fetcher="fetchCashDeals"
      @close="cashList = null"
    />
  </div>
</template>
